/**
 * n8n REST API Helper
 *
 * Connects to self-hosted n8n instances via their REST API.
 * API Documentation: https://docs.n8n.io/api/
 */

export interface N8nCredentials {
  instanceUrl: string; // e.g., https://n8n.example.com
  apiKey: string;
}

export interface N8nWorkflow {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  nodes: N8nNode[];
  connections: Record<string, N8nConnection[][]>;
  settings?: {
    executionOrder?: string;
    saveManualExecutions?: boolean;
    callerPolicy?: string;
    errorWorkflow?: string;
  };
  staticData?: Record<string, unknown>;
  tags?: N8nTag[];
}

export interface N8nNode {
  id: string;
  name: string;
  type: string;
  typeVersion: number;
  position: [number, number];
  parameters: Record<string, unknown>;
  credentials?: Record<string, { id: string; name: string }>;
  disabled?: boolean;
  notes?: string;
  notesInFlow?: boolean;
}

export interface N8nConnection {
  node: string;
  type: string;
  index: number;
}

export interface N8nTag {
  id: string;
  name: string;
}

export interface N8nExecution {
  id: string;
  finished: boolean;
  mode: string;
  retryOf?: string;
  retrySuccessId?: string;
  startedAt: string;
  stoppedAt?: string;
  workflowId: string;
  workflowData?: N8nWorkflow;
  data?: {
    resultData?: {
      runData?: Record<string, unknown[]>;
      error?: {
        message: string;
        stack?: string;
      };
    };
  };
  status: 'success' | 'error' | 'waiting' | 'running' | 'unknown';
}

export interface N8nCredential {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface N8nApiError {
  message: string;
  code?: string;
  httpCode?: number;
}

class N8nApiClient {
  private credentials: N8nCredentials | null = null;
  private useProxy: boolean = true; // Use backend proxy to avoid CORS

  setCredentials(credentials: N8nCredentials) {
    // Normalize URL - remove trailing slash
    this.credentials = {
      ...credentials,
      instanceUrl: credentials.instanceUrl.replace(/\/$/, ''),
    };
  }

  clearCredentials() {
    this.credentials = null;
  }

  isConfigured(): boolean {
    return !!(this.credentials?.instanceUrl && this.credentials?.apiKey);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    if (!this.isConfigured()) {
      throw new Error('n8n credentials not configured');
    }

    // Use backend proxy to avoid CORS issues
    if (this.useProxy) {
      const response = await fetch('/_api/integrations/n8n/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method,
          path,
          body,
        }),
      });

      if (!response.ok) {
        let errorMessage = `n8n API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // Ignore JSON parse errors
        }
        const error: N8nApiError = {
          message: errorMessage,
          httpCode: response.status,
        };
        throw error;
      }

      const data = await response.json();
      return data as T;
    }

    // Direct request (not used due to CORS, kept for reference)
    const url = `${this.credentials!.instanceUrl}/api/v1${path}`;
    const options: RequestInit = {
      method,
      headers: {
        'X-N8N-API-KEY': this.credentials!.apiKey,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `n8n API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // Ignore JSON parse errors
      }
      const error: N8nApiError = {
        message: errorMessage,
        httpCode: response.status,
      };
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // ============ Workflows ============

  async listWorkflows(): Promise<{ data: N8nWorkflow[] }> {
    return this.request<{ data: N8nWorkflow[] }>('GET', '/workflows');
  }

  async getWorkflow(id: string): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('GET', `/workflows/${id}`);
  }

  async createWorkflow(workflow: Partial<N8nWorkflow>): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('POST', '/workflows', workflow);
  }

  async updateWorkflow(id: string, workflow: Partial<N8nWorkflow>): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('PUT', `/workflows/${id}`, workflow);
  }

  async deleteWorkflow(id: string): Promise<void> {
    await this.request<void>('DELETE', `/workflows/${id}`);
  }

  async activateWorkflow(id: string): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('POST', `/workflows/${id}/activate`);
  }

  async deactivateWorkflow(id: string): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('POST', `/workflows/${id}/deactivate`);
  }

  async executeWorkflow(id: string, data?: Record<string, unknown>): Promise<N8nExecution> {
    return this.request<N8nExecution>('POST', `/workflows/${id}/run`, data);
  }

  // ============ Executions ============

  async listExecutions(options?: {
    workflowId?: string;
    status?: 'success' | 'error' | 'waiting';
    limit?: number;
  }): Promise<{ data: N8nExecution[] }> {
    const params = new URLSearchParams();
    if (options?.workflowId) params.append('workflowId', options.workflowId);
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());

    const query = params.toString();
    return this.request<{ data: N8nExecution[] }>('GET', `/executions${query ? `?${query}` : ''}`);
  }

  async getExecution(id: string): Promise<N8nExecution> {
    return this.request<N8nExecution>('GET', `/executions/${id}`);
  }

  async deleteExecution(id: string): Promise<void> {
    await this.request<void>('DELETE', `/executions/${id}`);
  }

  // ============ Credentials (metadata only) ============

  async listCredentials(): Promise<{ data: N8nCredential[] }> {
    return this.request<{ data: N8nCredential[] }>('GET', '/credentials');
  }

  // ============ Health Check ============

  async healthCheck(): Promise<boolean> {
    if (!this.credentials) {
      return false;
    }

    try {
      // Use dedicated test endpoint that accepts credentials in request body
      // This allows testing before credentials are saved to database
      const response = await fetch('/_api/integrations/n8n/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instanceUrl: this.credentials.instanceUrl,
          apiKey: this.credentials.apiKey,
        }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success === true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const n8nApi = new N8nApiClient();

// Export class for testing or multiple instances
export { N8nApiClient };

// Helper to create a minimal workflow
export function createEmptyWorkflow(name: string): Partial<N8nWorkflow> {
  return {
    name,
    nodes: [
      {
        id: 'start',
        name: 'Start',
        type: 'n8n-nodes-base.manualTrigger',
        typeVersion: 1,
        position: [250, 300],
        parameters: {},
      },
    ],
    connections: {},
    settings: {
      executionOrder: 'v1',
    },
  };
}

// Helper to create a webhook-triggered workflow
export function createWebhookWorkflow(name: string, path: string): Partial<N8nWorkflow> {
  return {
    name,
    nodes: [
      {
        id: 'webhook',
        name: 'Webhook',
        type: 'n8n-nodes-base.webhook',
        typeVersion: 1,
        position: [250, 300],
        parameters: {
          path,
          httpMethod: 'POST',
          responseMode: 'onReceived',
          responseData: 'allEntries',
        },
      },
    ],
    connections: {},
    settings: {
      executionOrder: 'v1',
    },
  };
}
